import React, { useEffect, useState } from 'react'
import { useAuthContext } from '@/lib/auth-context'
import { getMarketplaceCatalog, purchaseItem } from '@/lib/coreApi'

type MarketItem = {
  id: string
  sku: string
  kind: string
  title: string
  description?: string
  preview_url?: string
  price_credits: number
  meta: any
  owned?: boolean
}

const ParentMarketplace: React.FC = () => {
  const { session } = useAuthContext() as any
  const accessToken = session?.access_token

  const [items, setItems] = useState<MarketItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyItem, setBusyItem] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setError(null)
      try {
        const catalogRes = await getMarketplaceCatalog('guardian')
        if (!cancelled) setItems((catalogRes?.data?.items || []) as MarketItem[])
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not load marketplace.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [accessToken])

  async function handleBuy(item: MarketItem) {
    setBusyItem(item.id)
    setError(null)
    try {
      await purchaseItem({ item_id: item.id, quantity: 1 })
      // best-effort owned flag
      setItems((prev) => prev.map((p) => (p.id === item.id ? { ...p, owned: true } : p)))
    } catch (e: any) {
      setError(e?.message || 'Purchase failed')
    } finally {
      setBusyItem(null)
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div>
        <h1 className="text-base font-semibold">Family Marketplace (preview)</h1>
        <p className="text-xs text-slate-500">
          We’re still building out a full space for families. For now, you can collect a couple
          of simple digital items. More coming soon.
        </p>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {error && <div className="text-sm text-red-600">{error}</div>}

      {!loading && !items.length && (
        <div className="text-sm text-slate-500">
          No family items are available yet. Check back soon.
        </div>
      )}

      {items.length > 0 && (
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
          {items.map((item) => (
            <div key={item.id} className="border rounded-lg p-3 bg-white flex flex-col gap-2">
              {item.preview_url && (
                <img
                  src={item.preview_url}
                  alt={item.title}
                  className="w-full h-20 object-cover rounded"
                />
              )}
              <div className="font-medium text-sm">{item.title}</div>
              {item.description && (
                <p className="text-xs text-slate-600 line-clamp-3">{item.description}</p>
              )}
              <div className="text-[11px] text-slate-500 mt-auto">Price: {item.price_credits} credits</div>
              <button
                type="button"
                disabled={busyItem === item.id}
                onClick={() => handleBuy(item)}
                className="mt-2 text-xs px-2 py-1 rounded border disabled:opacity-50"
              >
                {busyItem === item.id ? 'Adding…' : 'Collect'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ParentMarketplace
